'use client';

import React, { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { solicitudService } from '@/lib/api/solicitudService';
import { Solicitud } from '@/types/solicitud';
import { SolicitudTable } from './SolicitudTable';
import { SolicitudFormModal } from './SolicitudFormModal';
import { SolicitudDetail } from './SolicitudDetail';
import { useSolicitudFilter, SolicitudFilterUI } from './solicitudFilters';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/lib/api/userService';
import { User } from '@/types/user';

const ContactadosManagement: React.FC = () => {
  const { user: authUser } = useAuth();
  const pathname = usePathname();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState<Solicitud | null>(null);

  const { filtered, baseList, ...filterProps } = useSolicitudFilter(solicitudes);

  const viewName = "Contactados";
  const fixedStatus = "CONTACTO";
  const areaResponsable = "COMERCIAL";

  const authSub = authUser?.sub ?? null;
  const inFlightRef = useRef(false);

  const fetchSolicitudes = async () => {
    if (!authSub) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      setIsLoading(true);
      setError(null);
      const response = await solicitudService.getContactados(authSub);
      if (response.data) {
        setSolicitudes(response.data || []);
      } else {
        setError(response.error || 'Error al cargar las solicitudes');
      }
    } catch (err: any) {
      setError(err?.message || 'Error al cargar las solicitudes');
      if (process.env.NODE_ENV === 'development') console.error('Error fetching solicitudes:', err);
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!authSub) return;
    let cancelled = false;
    userService.getUser(authSub).then((response) => {
      if (!cancelled && response.data) setCurrentUser(response.data);
    }).catch((err) => {
      if (!cancelled && process.env.NODE_ENV === 'development') console.error('Error cargando usuario:', err);
    });
    return () => { cancelled = true; };
  }, [authSub]);

  // Refetch al entrar a esta ruta (pathname) para evitar pantalla trabada al volver desde otro módulo
  useEffect(() => {
    if (!authSub) return;
    fetchSolicitudes();
    return () => { inFlightRef.current = false; };
  }, [authSub, pathname]);

  const handleCreate = () => {
    setIsModalOpen(true);
  };

  const handleEdit = (solicitud: Solicitud) => {
    if (!solicitud.id) return;
    setError(null);
    setSelectedSolicitud(solicitud);
    setIsDetailOpen(true);
  };

  const handleUpdateDetail = (updated: Solicitud) => {
    setSelectedSolicitud(updated);
    fetchSolicitudes();
  };

  const handleSubmit = async (data: Partial<Solicitud>) => {
    try {
      // Al crear una solicitud en Contactados, establecer estatus y fechas
      const solicitudData = {
        ...data,
        solicitudEstatus: fixedStatus,
        etapaEmbudo: fixedStatus,
        estatusEmbudo: fixedStatus,
        tipoDeNegocio: "ACTIVO",
        viewName: viewName,
        fechaContacto: data.fechaContacto || new Date().toISOString(),
      };

      if (data.id) {
        await solicitudService.updateFull(data.id, solicitudData);
      } else {
        await solicitudService.create(solicitudData);
      }
      setIsModalOpen(false);
      await fetchSolicitudes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar la solicitud');
      throw err;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contactados</h1>
          <p className="text-sm text-gray-500 mt-1">
            Solicitudes con estatus CONTACTO
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={fetchSolicitudes} disabled={isLoading}>
            {isLoading ? "Cargando…" : "Recargar"}
          </Button>
        </div>
      </div>

      {/* Filters - mismo que en Solicitudes */}
      <SolicitudFilterUI
        {...filterProps}
        filteredCount={filtered.length}
        baseCount={baseList.length}
        countLabel="solicitudes contactadas"
      />
      <div className="text-xs text-gray-500 -mt-2 px-1">Estatus: {fixedStatus} | Área: {areaResponsable}</div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <ErrorMessage message={error} onRetry={fetchSolicitudes} />
      ) : (
        <>
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
              <div className="text-sm font-semibold text-gray-900">Sin solicitudes contactadas</div>
              <div className="mt-1 text-sm text-gray-500">
                No hay solicitudes con estatus CONTACTO. Ajusta los filtros o crea una nueva solicitud.
              </div>
              <div className="mt-4 flex justify-center gap-2">
                <Button variant="secondary" onClick={filterProps.clearFilters}>
                  Limpiar búsqueda
                </Button>
              </div>
            </div>
          ) : (
            <>
              <SolicitudTable
                solicitudes={filtered}
                onEdit={handleEdit}
              />
              <div className="text-right text-xs text-gray-400 px-2">
                Total: {filtered.length} solicitudes contactadas
              </div>
            </>
          )}

          <SolicitudFormModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleSubmit}
          />

          <SolicitudDetail
            isOpen={isDetailOpen}
            onClose={() => setIsDetailOpen(false)}
            solicitud={selectedSolicitud}
            onUpdate={handleUpdateDetail}
            modulo="contactados"
          />
        </>
      )}
    </div>
  );
};

export default ContactadosManagement;
